import React, { useEffect } from "react";
import InputField from "../../components/Forms/InputField";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import SwitchField from "../../components/Forms/SwitchField";
import { useDispatch } from "react-redux";
import {
  endBlockingDialog,
  startBlockingDialog,
} from "../../Redux/loadingSlice";
import Axios from "../../config/axios";
import { toast } from "react-toastify";
import CommonDialog from "../../components/CommonDialog";
import {
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import AutocompleteInput from "../../components/Forms/AutocompleteInput";

const RepaymentTypeList = [
  { name: "MONTHLY", id: "MONTHLY" },
  { name: "WEEKLY", id: "WEEKLY" },
    { name: "FORTNIGHTLY", id: "FORTNIGHTLY" },
  { name: "DAILY", id: "DAILY" },
];

const DEFAULT_FORM_VALUES = {
  default: false,
  originationFeeAllowed: false,
  originationFeePercentage: null,
  originationFeeDollar: null,
  originationMaxDollarAmount: null,
  originationChargeType: null,
  prepaymentFeeAllowed: false,
  prepaymentFeePercentage: null,
  prepaymentFeeDollar: null,
  prepaymentMaxDollarAmount: null,
  prepaymentChargeType: null,
  mandatoryLending: false,
  maximumLoanAmount: null,
  loanFrequency: { name: "WEEKLY", id: "WEEKLY" },
  loanDuration: 10,
  serviceChargeType: "fees",
  unitFee: null,
  aprPer: null,
};

// Validation Schema
const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  mandatoryLending: Yup.boolean(),
  maximumLoanAmount: Yup.number().nullable(),

  // Origination Fee Validation
  originationFeeAllowed: Yup.boolean(),
  originationChargeType: Yup.string()
    .oneOf(["percentage", "dollar", null])
    .nullable()
    .when("originationFeeAllowed", {
      is: true,
      then: (schema) => schema.required("Please select origination fee type"),
      otherwise: (schema) => schema.notRequired(),
    }),

  originationFeePercentage: Yup.number()
    .nullable()
    .when(["originationFeeAllowed", "originationChargeType"], {
      is: (allowed, type) => allowed && type === "percentage",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .max(100, "Cannot exceed 100")
          .required("Origination Fee Percentage is required"),
    }),

  originationFeeDollar: Yup.number()
    .nullable()
    .when(["originationFeeAllowed", "originationChargeType"], {
      is: (allowed, type) => allowed && type === "dollar",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("Origination Fee Dollar is required"),
    }),

  originationMaxDollarAmount: Yup.number()
    .nullable()
    .when(["originationFeeAllowed", "originationChargeType"], {
      is: (allowed, type) => allowed && type === "percentage",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("Maximum Origination Fee is required"),
    }),

  // Prepayment Fee Validation
  prepaymentFeeAllowed: Yup.boolean(),
  prepaymentChargeType: Yup.string()
    .oneOf(["percentage", "dollar", null])
    .nullable()
    .when("prepaymentFeeAllowed", {
      is: true,
      then: (schema) => schema.required("Please select prepayment fee type"),
      otherwise: (schema) => schema.notRequired(),
    }),

  prepaymentFeePercentage: Yup.number()
    .nullable()
    .when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
      is: (allowed, type) => allowed && type === "percentage",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .max(100, "Cannot exceed 100")
          .required("Prepayment Fee Percentage is required"),
    }),

  prepaymentFeeDollar: Yup.number()
    .nullable()
    .when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
      is: (allowed, type) => allowed && type === "dollar",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("Prepayment Fee Dollar is required"),
    }),

  prepaymentMaxDollarAmount: Yup.number()
    .nullable()
    .when(["prepaymentFeeAllowed", "prepaymentChargeType"], {
      is: (allowed, type) => allowed && type === "percentage",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("Maximum Prepayment Fee is required"),
    }),

  // Loan Details
  loanDuration: Yup.number()
    .typeError("Must be a number")
    .min(1, "Must be at least 1")
    .required("Loan Duration is required"),

  loanFrequency: Yup.object()
    .required("Please select loan frequency")
    .typeError("Please select loan frequency"),

  serviceChargeType: Yup.string()
    .oneOf(["fees", "apr_percentage"], "Invalid Service Charge Type")
    .required("Service Charge Type is required"),

  unitFee: Yup.number()
    .nullable()
    .when("serviceChargeType", {
      is: "fees",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("Unit Fee is required"),
    }),

  aprPer: Yup.number()
    .nullable()
    .when("serviceChargeType", {
      is: "apr_percentage",
      then: (schema) =>
        schema
          .typeError("Must be a number")
          .min(0, "Must be zero or positive")
          .required("APR % is required"),
    }),
});

function EditLoanProfile({ open, onClose, editData, getProfileList }) {
  const dispatch = useDispatch();

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  });

  // Watch all relevant fields
  const originationFeeAllowed = watch("originationFeeAllowed");
  const prepaymentFeeAllowed = watch("prepaymentFeeAllowed");
  const serviceChargeType = watch("serviceChargeType");
  const originationChargeType = watch("originationChargeType");
  const prepaymentChargeType = watch("prepaymentChargeType");

  // Reset origination fee fields when toggle is OFF
  useEffect(() => {
    if (!originationFeeAllowed) {
      setValue("originationChargeType", null, { shouldValidate: false });
      setValue("originationFeePercentage", null, { shouldValidate: false });
      setValue("originationFeeDollar", null, { shouldValidate: false });
      setValue("originationMaxDollarAmount", null, { shouldValidate: false });
    } else if (originationFeeAllowed && !originationChargeType) {
      // Set default charge type when enabling
      setValue("originationChargeType", "dollar", { shouldValidate: false });
    }
  }, [originationFeeAllowed, originationChargeType, setValue]);

  // Reset prepayment fee fields when toggle is OFF
  useEffect(() => {
    if (!prepaymentFeeAllowed) {
      setValue("prepaymentChargeType", null, { shouldValidate: false });
      setValue("prepaymentFeePercentage", null, { shouldValidate: false });
      setValue("prepaymentFeeDollar", null, { shouldValidate: false });
      setValue("prepaymentMaxDollarAmount", null, { shouldValidate: false });
    } else if (prepaymentFeeAllowed && !prepaymentChargeType) {
      // Set default charge type when enabling
      setValue("prepaymentChargeType", "dollar", { shouldValidate: false });
    }
  }, [prepaymentFeeAllowed, prepaymentChargeType, setValue]);

  // Reset fields when switching between origination charge types
  useEffect(() => {
    if (!originationFeeAllowed || !originationChargeType) return;

    if (originationChargeType === "percentage") {
      setValue("originationFeeDollar", null, { shouldValidate: false });
    } else if (originationChargeType === "dollar") {
      setValue("originationFeePercentage", null, { shouldValidate: false });
      setValue("originationMaxDollarAmount", null, { shouldValidate: false });
    }
  }, [originationChargeType, originationFeeAllowed, setValue]);

  // Reset fields when switching between prepayment charge types
  useEffect(() => {
    if (!prepaymentFeeAllowed || !prepaymentChargeType) return;

    if (prepaymentChargeType === "percentage") {
      setValue("prepaymentFeeDollar", null, { shouldValidate: false });
    } else if (prepaymentChargeType === "dollar") {
      setValue("prepaymentFeePercentage", null, { shouldValidate: false });
      setValue("prepaymentMaxDollarAmount", null, { shouldValidate: false });
    }
  }, [prepaymentChargeType, prepaymentFeeAllowed, setValue]);

  // Reset service charge fields when switching types
  useEffect(() => {
    if (serviceChargeType === "fees") {
      setValue("aprPer", null, { shouldValidate: false });
      // if (watch("unitFee") === null) {
      //   setValue("unitFee", 10, { shouldValidate: false });
      // }
    } else if (serviceChargeType === "apr_percentage") {
      setValue("unitFee", null, { shouldValidate: false });
      // if (watch("aprPer") === null) {
      //   setValue("aprPer", 10, { shouldValidate: false });
      // }
    }
  }, [serviceChargeType, setValue, watch]);

  const onSubmit = async (data) => {
    try {
      dispatch(startBlockingDialog());

      // Prepare clean data for API
      const cleanData = {
        ...data,
        loanFrequency: data.loanFrequency.id,

        // Ensure null values for disabled fields
        originationFeePercentage: data.originationFeeAllowed && data.originationChargeType === "percentage"
          ? data.originationFeePercentage
          : null,
        originationFeeDollar: data.originationFeeAllowed && data.originationChargeType === "dollar"
          ? data.originationFeeDollar
          : null,
        originationMaxDollarAmount: data.originationFeeAllowed && data.originationChargeType === "percentage"
          ? data.originationMaxDollarAmount
          : null,
        originationChargeType: data.originationFeeAllowed ? data.originationChargeType : null,

        prepaymentFeePercentage: data.prepaymentFeeAllowed && data.prepaymentChargeType === "percentage"
          ? data.prepaymentFeePercentage
          : null,
        prepaymentFeeDollar: data.prepaymentFeeAllowed && data.prepaymentChargeType === "dollar"
          ? data.prepaymentFeeDollar
          : null,
        prepaymentMaxDollarAmount: data.prepaymentFeeAllowed && data.prepaymentChargeType === "percentage"
          ? data.prepaymentMaxDollarAmount
          : null,
        prepaymentChargeType: data.prepaymentFeeAllowed ? data.prepaymentChargeType : null,

        unitFee: data.serviceChargeType === "fees" ? data.unitFee : 0,
        aprPer: data.serviceChargeType === "apr_percentage" ? data.aprPer : 0,
      };

      const response = await Axios.patch(
        `/loan-profile/admin/${editData.id}`,
        cleanData
      );

      if (response?.data?.success) {
        toast.success(response?.data?.message || "Loan profile updated successfully");
        onClose();
        getProfileList();
      } else {
        toast.error(response?.data?.message || "Failed to update loan profile");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      dispatch(endBlockingDialog());
    }
  };

  useEffect(() => {
    if (editData && open) {
      const formData = {
        name: editData.name || "",
        default: editData.default || false,
        mandatoryLending: editData.mandatoryLending || false,
        maximumLoanAmount: editData.maximumLoanAmount || null,

        // Origination Fee
        originationFeeAllowed: editData.originationFeeAllowed || false,
        originationChargeType: editData.originationChargeType || null,
        originationFeePercentage: editData.originationFeePercentage || null,
        originationFeeDollar: editData.originationFeeDollar || null,
        originationMaxDollarAmount: editData.originationMaxDollarAmount || null,

        // Prepayment Fee
        prepaymentFeeAllowed: editData.prepaymentFeeAllowed || false,
        prepaymentChargeType: editData.prepaymentChargeType || null,
        prepaymentFeePercentage: editData.prepaymentFeePercentage || null,
        prepaymentFeeDollar: editData.prepaymentFeeDollar || null,
        prepaymentMaxDollarAmount: editData.prepaymentMaxDollarAmount || null,

        // Loan Details
        loanDuration: editData.loanDuration || 10,
        loanFrequency: RepaymentTypeList.find((r) => r.id === editData.loanFrequency) ||
          RepaymentTypeList.find((r) => r.id === "WEEKLY"),

        // Service Charge
        serviceChargeType: editData.serviceChargeType || "fees",
        unitFee: editData.unitFee || null,
        aprPer: editData.aprPer || null,
      };

      reset(formData);
    }
  }, [editData, open, reset]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset(DEFAULT_FORM_VALUES);
    }
  }, [open, reset]);

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="Edit Loan Profile"
      handleSubmit={() => handleSubmit(onSubmit)()}
    >
      <Grid container spacing={2}>
        {/* Profile Name */}
        <Grid item xs={12} md={6}>
          <InputField
            control={control}
            name="name"
            label="Profile Name"
            placeholder="Enter profile name"
          />
        </Grid>

        {/* Origination Fee Toggle */}
        <Grid item xs={12}>
          <SwitchField
            control={control}
            name="originationFeeAllowed"
            label="Origination Fee"
          />
        </Grid>

        {/* Origination Fee Fields */}
        {originationFeeAllowed && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "white !important" }}>
                  Origination Fee Type
                </FormLabel>
                <Controller
                  name="originationChargeType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ mt: -0.5, ml: 1 }}>
                      <FormControlLabel
                        value="dollar"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Fixed Dollar ($)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                      <FormControlLabel
                        value="percentage"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Percentage (%)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>

            {originationChargeType === "percentage" && (
              <Grid item xs={12} md={6}>
                <InputField
                  control={control}
                  name="originationFeePercentage"
                  label="Origination Fee (%)"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter percentage"
                />
                <InputField
                  control={control}
                  name="originationMaxDollarAmount"
                  label="Maximum Origination Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter maximum fee"
                />
              </Grid>
            )}

            {originationChargeType === "dollar" && (
              <Grid item xs={12} md={6}>
                <InputField
                  control={control}
                  name="originationFeeDollar"
                  label="Origination Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter fee amount"
                />
              </Grid>
            )}
          </>
        )}

        {/* Prepayment Fee Toggle */}
        <Grid item xs={12}>
          <SwitchField
            control={control}
            name="prepaymentFeeAllowed"
            label="Prepayment Fee"
          />
        </Grid>

        {/* Prepayment Fee Fields */}
        {prepaymentFeeAllowed && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel sx={{ color: "white !important" }}>
                  Prepayment Fee Type
                </FormLabel>
                <Controller
                  name="prepaymentChargeType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup {...field} row sx={{ mt: -0.5, ml: 1 }}>
                      <FormControlLabel
                        value="dollar"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Fixed Dollar ($)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                      <FormControlLabel
                        value="percentage"
                        control={<Radio sx={{ color: "white" }} />}
                        label="Percentage (%)"
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                          "& .MuiFormControlLabel-root": {
                            border: "none",
                            margin: "0px !important",
                            padding: "0px !important",
                          },
                        }}
                      />
                    </RadioGroup>
                  )}
                />
              </FormControl>
            </Grid>

            {prepaymentChargeType === "percentage" && (
              <Grid item xs={12} md={6}>
                <InputField
                  control={control}
                  name="prepaymentFeePercentage"
                  label="Prepayment Fee (%)"
                  type="number"
                  isPercentages={true}
                  placeholder="Enter percentage"
                />
                <InputField
                  control={control}
                  name="prepaymentMaxDollarAmount"
                  label="Maximum Prepayment Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter maximum fee"
                />
              </Grid>
            )}

            {prepaymentChargeType === "dollar" && (
              <Grid item xs={12} md={6}>
                <InputField
                  control={control}
                  name="prepaymentFeeDollar"
                  label="Prepayment Fee ($)"
                  type="number"
                  isCurrency={true}
                  placeholder="Enter fee amount"
                />
              </Grid>
            )}
          </>
        )}

        {/* Mandatory Lending */}
        <Grid item xs={12} md={6}>
          <SwitchField
            control={control}
            name="mandatoryLending"
            label="Is Mandatory Lending?"
          />
        </Grid>

        {/* Maximum Loan Amount */}
        <Grid item xs={12}>
          <InputField
            control={control}
            name="maximumLoanAmount"
            label="Maximum Loan Amount ($)"
            type="number"
            isCurrency={true}
            placeholder="Enter maximum loan amount"
          />
        </Grid>

        {/* Loan Frequency */}
        <Grid item xs={12} md={6}>
          <AutocompleteInput
            control={control}
            options={RepaymentTypeList}
            name="loanFrequency"
            label="Loan Repayment Frequency"
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        {/* Loan Duration */}
        <Grid item xs={12} md={6}>
          <InputField
            control={control}
            name="loanDuration"
            label="Loan Installment"
            type="number"
            placeholder="Enter Loan Installment"
          />
        </Grid>

        {/* Service Charge Type */}
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel sx={{ color: "white !important" }}>
              Service Charge Type
            </FormLabel>
            <Controller
              name="serviceChargeType"
              control={control}
              render={({ field }) => (
                <RadioGroup {...field} row>
                  <FormControlLabel
                    value="fees"
                    control={<Radio sx={{ color: "white" }} />}
                    label="Fees for every $10"
                    sx={{
                      color: "white",
                      "& .MuiTypography-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                      "& .MuiFormControlLabel-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                    }}
                  />
                  <FormControlLabel
                    value="apr_percentage"
                    control={<Radio sx={{ color: "white" }} />}
                    label="APR %"
                    sx={{
                      color: "white",
                      "& .MuiTypography-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                      "& .MuiFormControlLabel-root": {
                        border: "none",
                        margin: "0px !important",
                        padding: "0px !important",
                      },
                    }}
                  />
                </RadioGroup>
              )}
            />
          </FormControl>
        </Grid>

        {serviceChargeType === "fees" && (
          <Grid item xs={12}>
            <InputField
              control={control}
              name="unitFee"
              label="Fees for every $10"
              type="number"
              isCurrency={true}
              placeholder="Enter unit fee"
            />
          </Grid>
        )}

        {serviceChargeType === "apr_percentage" && (
          <Grid item xs={12}>
            <InputField
              control={control}
              name="aprPer"
              label="APR %"
              type="number"
              isPercentages={true}
              placeholder="Enter APR percentage"
            />
          </Grid>
        )}
      </Grid>
    </CommonDialog>
  );
}

export default EditLoanProfile;